<script setup lang="ts">
import { defineComponent, h, ref, watchEffect, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { PopupBasicHeader } from 'wepin-vue-component'
import SelectParentAccount from './SelectParentAccount.vue'
import { WrappedNetworkListItem, BasicBottomButtons as BBB } from '@/components'
import { useBreakPointsClass } from '@/composables'
import { defaultBreakPoints, defaultCoinIcon } from '@/const'
import { useWalletStore } from '@/stores'
import type { Account, Promisable } from '@/types'

const emits = defineEmits<{
  (event: 'close'): void
  (event: 'click-account', account: Account): void
}>()

const { t } = useI18n()
const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const walletStore = useWalletStore()
const router = useRouter()

const networkAccountList: Ref<Account[] | undefined> = ref()
watchEffect(async () => {
  networkAccountList.value = await walletStore.getAccountListFilteringByNetwork(
    { network: router.currentRoute.value.query.network as string },
  )
})

const onExit = () => {
  emits('close')
}
const selected = ref<Account>()
const onSelectAccount = (account: Account) => {
  if (selected.value?.id.toString() === account.id.toString()) {
    selected.value = undefined
    return
  }
  selected.value = account
}
const onClickAccount = (account: Account) => {
  emits('click-account', account)
}
</script>
<script lang="ts">
export const createSelectParentAccount = (params: {
  onClickAccount: (account: Account) => Promisable<void>
}) => {
  return defineComponent<typeof SelectParentAccount>((props) => {
    return () =>
      h(SelectParentAccount, {
        'onClick-account': params.onClickAccount,
        ...props,
      })
  })
}
</script>
<template>
  <PopupBasicHeader :title="t('create_account.add_account')" :exit="onExit" />
  <div class="select-parent-account" :class="[breakPointsClass]">
    <div class="notice-text">
      {{ t('create_account.selected_network_title') }}
      <span v-text="t('create_account.selected_network_sub')" />
    </div>
    <div class="select-fee-account">
      <div
        v-for="account of networkAccountList"
        :key="account.id.toString()"
        class="fee-account"
        @click="onSelectAccount(account)"
      >
        <WrappedNetworkListItem
          :ticker="account.coin?.name"
          :symbol="account.coin?.symbol"
          :address="account.address.shirinkAddress()"
          :network-src="account.coin?.iconUrl ?? defaultCoinIcon"
          :width="breakPointsClass === 'mobile' ? '100%' : '400px'"
          :selected="account.id.toString() === selected?.id.toString()"
          required
        />
      </div>
    </div>
    <BBB
      :disabled="!selected"
      @confirm="onClickAccount(selected!)"
      @cancel="onExit"
    />
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.select-parent-account {
  padding: 0 30px;
  margin: 0 auto 30px auto;
  display: flex;
  flex-direction: column;
  & > .notice-text {
    width: 400px;
    margin-bottom: 10px;
    @include typeface('Title_18');
    & > span {
      display: block;
      margin-top: 10px;
      @include typeface('Paragraph_16_r');
    }
  }
  .select-fee-account {
    width: 100%;
    display: flex;
    flex-flow: column;
    gap: 10px;
    margin-top: 20px;
    margin-bottom: 30px;
  }
}

.select-parent-account.mobile {
  margin: 0;
  padding: 0 20px;
}
</style>
